// src/features/operational/services/subjectService.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc,
    query, 
    where, 
    orderBy, 
    serverTimestamp 
  } from "firebase/firestore";
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL,
    deleteObject 
  } from "firebase/storage";
  import { db, storage } from "../../../firebase/config";
  import { normalizeSubjectDocument } from "../../../utils/subjectDocument";
  
  // Fetch user subjects
  export const fetchUserSubjects = async (userId) => {
    try {
      const subjectsRef = collection(db, "subjects");
      const subjectsById = new Map();

      const primaryQuery = query(
        subjectsRef,
        where("userId", "==", userId),
        orderBy("createdAt", "desc")
      );

      const primarySnapshot = await getDocs(primaryQuery);

      primarySnapshot.forEach((docSnapshot) => {
        subjectsById.set(docSnapshot.id, {
          id: docSnapshot.id,
          ...docSnapshot.data()
        });
      });

      // Backward compatibility for older subject documents that used createdBy
      // before userId was written consistently.
      if (subjectsById.size === 0) {
        const fallbackQuery = query(
          subjectsRef,
          where("createdBy", "==", userId),
          orderBy("createdAt", "desc")
        );

        const fallbackSnapshot = await getDocs(fallbackQuery);

        fallbackSnapshot.forEach((docSnapshot) => {
          subjectsById.set(docSnapshot.id, {
            id: docSnapshot.id,
            ...docSnapshot.data()
          });
        });
      }

      return Array.from(subjectsById.values());
    } catch (error) {
      console.error("Error fetching user subjects:", error);
      throw error;
    }
  };
  
  // Add a new subject
  export const addSubject = async (userId, subjectData) => {
    try {
      const subjectsRef = collection(db, "subjects");
      const normalizedSubject = normalizeSubjectDocument(subjectData, { userId });
      
      const docRef = await addDoc(subjectsRef, {
        ...normalizedSubject,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      const newSubject = await getDoc(docRef);
      
      return {
        id: newSubject.id,
        ...newSubject.data()
      };
    } catch (error) {
      console.error("Error adding subject:", error);
      throw error;
    }
  };
  
  // Update a subject
  export const updateSubject = async (subjectId, subjectData) => {
    try {
      const subjectRef = doc(db, "subjects", subjectId);
      const currentSubject = await getDoc(subjectRef);

      if (!currentSubject.exists()) {
        throw new Error("Subject not found");
      }

      const normalizedSubject = normalizeSubjectDocument({
        ...currentSubject.data(),
        ...subjectData
      });
      
      await updateDoc(subjectRef, {
        ...normalizedSubject,
        updatedAt: serverTimestamp()
      });
      
      const updatedSubject = await getDoc(subjectRef);
      
      return {
        id: updatedSubject.id,
        ...updatedSubject.data()
      };
    } catch (error) {
      console.error("Error updating subject:", error);
      throw error;
    }
  };
  
  // Delete a subject
  export const deleteSubject = async (subjectId) => {
    try {
      await deleteDoc(doc(db, "subjects", subjectId));
      return { success: true };
    } catch (error) {
      console.error("Error deleting subject:", error);
      throw error;
    }
  };
  
  // Fetch subject plans
  export const fetchSubjectPlans = async (subjectId) => {
    try {
      const plansRef = collection(db, "subjects", subjectId, "plans");
      const q = query(plansRef, orderBy("createdAt", "desc"));
      
      const querySnapshot = await getDocs(q);
      const plans = [];
      
      querySnapshot.forEach((doc) => {
        plans.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return plans;
    } catch (error) {
      console.error("Error fetching subject plans:", error);
      throw error;
    }
  };
  
  // Add a new plan
  export const addPlan = async (subjectId, planData) => {
    try {
      const plansRef = collection(db, "subjects", subjectId, "plans");
      
      const docRef = await addDoc(plansRef, {
        ...planData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: planData.status || 'not-started'
      });
      
      const newPlan = await getDoc(docRef);
      
      return {
        id: newPlan.id,
        ...newPlan.data()
      };
    } catch (error) {
      console.error("Error adding plan:", error);
      throw error;
    }
  };
  
  // Update a plan
  export const updatePlan = async (subjectId, planId, planData) => {
    try {
      const planRef = doc(db, "subjects", subjectId, "plans", planId);
      
      await updateDoc(planRef, {
        ...planData,
        updatedAt: serverTimestamp()
      });
      
      const updatedPlan = await getDoc(planRef);
      
      return {
        id: updatedPlan.id,
        ...updatedPlan.data()
      };
    } catch (error) {
      console.error("Error updating plan:", error);
      throw error;
    }
  };
  
  // Delete a plan
  export const deletePlan = async (subjectId, planId) => {
    try {
      await deleteDoc(doc(db, "subjects", subjectId, "plans", planId));
      return { success: true };
    } catch (error) {
      console.error("Error deleting plan:", error);
      throw error;
    }
  };
  
  // Upload plan document
  export const uploadPlanDocument = async (userId, subjectId, planId, file) => {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExtension}`;
      const filePath = `users/${userId}/subjects/${subjectId}/plans/${planId}/${fileName}`;
      
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update the plan document reference
      const planRef = doc(db, "subjects", subjectId, "plans", planId);
      await updateDoc(planRef, {
        documentUrl: downloadURL,
        documentPath: filePath,
        updatedAt: serverTimestamp()
      });
      
      return {
        url: downloadURL,
        path: filePath
      };
    } catch (error) {
      console.error("Error uploading plan document:", error);
      throw error;
    }
  };
  
  // Delete plan document
  export const deletePlanDocument = async (subjectId, planId, filePath) => {
    try {
      // Delete from storage
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      
      // Update the plan reference
      const planRef = doc(db, "subjects", subjectId, "plans", planId);
      await updateDoc(planRef, {
        documentUrl: null,
        documentPath: null,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting plan document:", error);
      throw error;
    }
  };
